import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Loader2, Minus, Plus } from 'lucide-react';
import { toast } from '../hooks/use-toast';

interface Product {
  id: string;
  productId?: number;
  name: string;
  sku: string;
  category: string;
  unitPrice: number;
  productType?: "simple" | "variant" | "hybrid";
  variants?: Array<{
    size: string;
    sku: string;
    price: number;
    stockLevel: number;
    minThreshold: number;
    maxCapacity: number;
    productId?: number;
  }>;
  baseStockLevel?: number;
  baseMinThreshold?: number;
  baseMaxCapacity?: number;
}

interface InventoryData {
  size?: string;
  sku?: string;
  price?: number;
  stockLevel?: number;
  minThreshold?: number;
  maxCapacity?: number;
  // Legacy fields for backward compatibility
  currentStock?: number;
  minStock?: number;
  maxStock?: number;
}

interface EditStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  inventoryData: InventoryData | null;
  onSave: (productId: string, stockData: InventoryData) => Promise<void>;
  isLoading?: boolean;
}

export const EditStockModal: React.FC<EditStockModalProps> = ({
  isOpen,
  onClose,
  product,
  inventoryData,
  onSave,
  isLoading = false
}) => {
  const [stockType, setStockType] = useState<'base' | 'variant'>('base');
  const [selectedVariant, setSelectedVariant] = useState<string>('');
  const [formData, setFormData] = useState<InventoryData>({
    size: "",
    sku: "",
    price: 0,
    stockLevel: 0,
    minThreshold: 10,
    maxCapacity: 100
  });
  const [errors, setErrors] = useState<Partial<InventoryData>>({});

  // Initialize form data when modal opens or inventory data changes
  useEffect(() => {
    if (product && isOpen) {
      // Initialize based on product type
      if (product.productType === "simple") {
        setStockType('base');
        setFormData({
          stockLevel: product.baseStockLevel || 0,
          minThreshold: product.baseMinThreshold || 10,
          maxCapacity: product.baseMaxCapacity || 100
        });
      } else if (product.productType === "variant") {
        setStockType('variant');
        if (product.variants && product.variants.length > 0) {
          setSelectedVariant(product.variants[0].size);
          const firstVariant = product.variants[0];
          setFormData({
            size: firstVariant.size,
            sku: firstVariant.sku,
            price: firstVariant.price,
            stockLevel: firstVariant.stockLevel,
            minThreshold: firstVariant.minThreshold,
            maxCapacity: firstVariant.maxCapacity
          });
        }
      } else if (product.productType === "hybrid") {
        // Default to base stock for hybrid products
        setStockType('base');
        setFormData({
          price: product.unitPrice,
          stockLevel: product.baseStockLevel || 0,
          minThreshold: product.baseMinThreshold || 10,
          maxCapacity: product.baseMaxCapacity || 100
        });
      }
    }
  }, [product, isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        size: "1 x 1",
        sku: "",
        price: 0,
        stockLevel: 0,
        minThreshold: 10,
        maxCapacity: 100
      });
      setErrors({});
    }
  }, [isOpen]);

  const handleStockTypeChange = (type: 'base' | 'variant') => {
    setStockType(type);
    if (type === 'base' && product) {
      setFormData({
        stockLevel: product.baseStockLevel || 0,
        minThreshold: product.baseMinThreshold || 10,
        maxCapacity: product.baseMaxCapacity || 100
      });
    } else if (type === 'variant' && product?.variants && product.variants.length > 0) {
      const firstVariant = product.variants[0];
      setSelectedVariant(firstVariant.size);
      setFormData({
        size: firstVariant.size,
        sku: firstVariant.sku,
        price: firstVariant.price,
        stockLevel: firstVariant.stockLevel,
        minThreshold: firstVariant.minThreshold,
        maxCapacity: firstVariant.maxCapacity
      });
    }
  };

  const handleVariantChange = (variantSize: string) => {
    setSelectedVariant(variantSize);
    if (product?.variants) {
      const variant = product.variants.find(v => v.size === variantSize);
      if (variant) {
        setFormData({
          size: variant.size,
          sku: variant.sku,
          price: variant.price,
          stockLevel: variant.stockLevel,
          minThreshold: variant.minThreshold,
          maxCapacity: variant.maxCapacity
        });
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<InventoryData> = {};

    // Validate variant fields only for variant/hybrid products
    if (product?.productType !== "simple") {
      if (!formData.size?.trim()) {
        newErrors.size = 'Size is required';
      }

      if (!formData.sku?.trim()) {
        newErrors.sku = 'SKU is required';
      }

      if (formData.price === undefined || formData.price < 0) {
        newErrors.price = 'Price cannot be negative';
      }
    }

    // Stock level validation - allow both increase and decrease
    // No validation needed as users should be able to adjust stock levels as needed

    // Validate min threshold
    if (formData.minThreshold !== undefined && formData.minThreshold < 0) {
      newErrors.minThreshold = 'Minimum threshold cannot be negative';
    }

    // Validate max capacity
    if (formData.maxCapacity !== undefined && formData.maxCapacity < 0) {
      newErrors.maxCapacity = 'Maximum capacity cannot be negative';
    }

    // Validate min <= max
    if (formData.minThreshold !== undefined && formData.maxCapacity !== undefined && 
        formData.minThreshold > formData.maxCapacity) {
      newErrors.minThreshold = 'Minimum threshold cannot be greater than maximum capacity';
      newErrors.maxCapacity = 'Maximum capacity cannot be less than minimum threshold';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof InventoryData, value: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleStepperChange = (field: keyof InventoryData, delta: number) => {
    if (field === 'stockLevel' && delta < 0) {
      // Don't allow decreasing stock level
      return;
    }
    const currentValue = formData[field] as number;
    const newValue = Math.max(0, currentValue + delta);
    handleInputChange(field, newValue);
  };

  const handleSave = async () => {
    if (!product || !validateForm()) {
      return;
    }

    try {
      await onSave(product.id, formData);
      onClose();
      toast({
        title: "Success",
        description: "Stock levels updated successfully!",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update stock levels. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!product) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto w-full mx-4"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {product.productType === "simple" 
              ? `Edit Stock for ${product.name}`
              : stockType === "base"
                ? `Edit Base Stock for ${product.name}`
                : `Edit Variant Stock for ${product.name}${selectedVariant ? ` (${selectedVariant})` : ''}`
            }
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Product Info */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-sm text-gray-600">
              {product.productId && (
                <div><strong>Product ID:</strong> {product.productId}</div>
              )}
              <div><strong>SKU:</strong> {product.sku}</div>
              <div><strong>Category:</strong> {product.category}</div>
              <div><strong>Unit Price:</strong> ${product.unitPrice.toFixed(2)}</div>
            </div>
          </div>

          {/* Stock Type Selector - Only show for hybrid products */}
          {product.productType === "hybrid" && (
            <div className="space-y-2">
              <Label htmlFor="stockType">Stock Type</Label>
              <Select value={stockType} onValueChange={handleStockTypeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select stock type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="base">Base Product Stock</SelectItem>
                  <SelectItem value="variant">Variant Stock</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Variant Selector - Only show for variant products or when variant type is selected */}
          {(product.productType === "variant" || (product.productType === "hybrid" && stockType === "variant")) && product.variants && product.variants.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="variant">Variant Size</Label>
              <Select value={selectedVariant} onValueChange={handleVariantChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select variant size" />
                </SelectTrigger>
                <SelectContent>
                  {product.variants.map((variant) => (
                    <SelectItem key={variant.size} value={variant.size}>
                      {variant.size} - ${variant.price.toFixed(2)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Variant Info - Show variant product ID when variant is selected */}
          {selectedVariant && product.variants && (
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-sm text-gray-600">
                {(() => {
                  const variant = product.variants.find(v => v.size === selectedVariant);
                  return variant && variant.productId ? (
                    <div><strong>Variant Product ID:</strong> {variant.productId}</div>
                  ) : null;
                })()}
              </div>
            </div>
          )}

          {/* Size - Only show for variant/hybrid products when variant is selected */}
          {(product.productType === "variant" || (product.productType === "hybrid" && stockType === "variant")) && (
            <div className="space-y-2">
              <Label htmlFor="size" className="text-sm font-medium">
                Size
              </Label>
              <Input
                id="size"
                type="text"
                value={formData.size || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, size: e.target.value }))}
                className={errors.size ? 'border-red-500' : ''}
                disabled={isLoading}
                placeholder="e.g., S, M, L, 10x12"
              />
              {errors.size && (
                <p className="text-sm text-red-500">{errors.size}</p>
              )}
            </div>
          )}

          {/* SKU - Only show for variant/hybrid products */}
          {product.productType !== "simple" && (
            <div className="space-y-2">
              <Label htmlFor="sku" className="text-sm font-medium">
                SKU
              </Label>
              <Input
                id="sku"
                type="text"
                value={formData.sku || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                className={errors.sku ? 'border-red-500' : ''}
                disabled={isLoading}
                placeholder="Stock Keeping Unit"
              />
              {errors.sku && (
                <p className="text-sm text-red-500">{errors.sku}</p>
              )}
            </div>
          )}

          {/* Price - Only show for variant/hybrid products */}
          {product.productType !== "simple" && (
            <div className="space-y-2">
              <Label htmlFor="price" className="text-sm font-medium">
                Price
              </Label>
              <div className="flex items-center space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleStepperChange('price', -1)}
                  disabled={isLoading || (formData.price || 0) <= 0}
                  className="h-8 w-8 p-0"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  id="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price || 0}
                  onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                  className={`text-center ${errors.price ? 'border-red-500' : ''}`}
                  disabled={isLoading}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleStepperChange('price', 1)}
                  disabled={isLoading}
                  className="h-8 w-8 p-0"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {errors.price && (
                <p className="text-sm text-red-500">{errors.price}</p>
              )}
            </div>
          )}

          {/* Stock Level */}
          <div className="space-y-2">
            <Label htmlFor="stockLevel" className="text-sm font-medium">
              Add Stock (Current: {inventoryData?.stockLevel || inventoryData?.currentStock || 0})
            </Label>
            <div className="flex items-center space-x-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={true}
                className="h-8 w-8 p-0 opacity-50 cursor-not-allowed"
                title="Stock can only be increased, not decreased"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Input
                id="stockLevel"
                type="number"
                min={inventoryData?.stockLevel || inventoryData?.currentStock || 0}
                value={formData.stockLevel || 0}
                onChange={(e) => handleInputChange('stockLevel', parseInt(e.target.value) || 0)}
                className={`text-center ${errors.stockLevel ? 'border-red-500' : ''}`}
                disabled={isLoading}
                placeholder={`Min: ${inventoryData?.stockLevel || inventoryData?.currentStock || 0}`}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleStepperChange('stockLevel', 1)}
                disabled={isLoading}
                className="h-8 w-8 p-0"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              You can only add stock to the current level of {inventoryData?.stockLevel || inventoryData?.currentStock || 0} units
            </p>
            {errors.stockLevel && (
              <p className="text-sm text-red-500">{errors.stockLevel}</p>
            )}
          </div>

          {/* Min Threshold */}
          <div className="space-y-2">
            <Label htmlFor="minThreshold" className="text-sm font-medium">
              Minimum Threshold
            </Label>
            <div className="flex items-center space-x-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleStepperChange('minThreshold', -1)}
                disabled={isLoading || (formData.minThreshold || 0) <= 0}
                className="h-8 w-8 p-0"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Input
                id="minThreshold"
                type="number"
                min="0"
                value={formData.minThreshold || 0}
                onChange={(e) => handleInputChange('minThreshold', parseInt(e.target.value) || 0)}
                className={`text-center ${errors.minThreshold ? 'border-red-500' : ''}`}
                disabled={isLoading}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleStepperChange('minThreshold', 1)}
                disabled={isLoading}
                className="h-8 w-8 p-0"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {errors.minThreshold && (
              <p className="text-sm text-red-500">{errors.minThreshold}</p>
            )}
          </div>

          {/* Max Capacity */}
          <div className="space-y-2">
            <Label htmlFor="maxCapacity" className="text-sm font-medium">
              Maximum Capacity
            </Label>
            <div className="flex items-center space-x-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleStepperChange('maxCapacity', -1)}
                disabled={isLoading || (formData.maxCapacity || 0) <= 0}
                className="h-8 w-8 p-0"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Input
                id="maxCapacity"
                type="number"
                min="0"
                value={formData.maxCapacity || 0}
                onChange={(e) => handleInputChange('maxCapacity', parseInt(e.target.value) || 0)}
                className={`text-center ${errors.maxCapacity ? 'border-red-500' : ''}`}
                disabled={isLoading}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleStepperChange('maxCapacity', 1)}
                disabled={isLoading}
                className="h-8 w-8 p-0"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {errors.maxCapacity && (
              <p className="text-sm text-red-500">{errors.maxCapacity}</p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isLoading || Object.keys(errors).length > 0}
            className="min-w-[100px]"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditStockModal;
